import React, { useState, useMemo, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import { getAffiliationColor, getOverride, getEstimatedVotesPercent, getRaceEevpSentence } from "../helpers";
import WidgetName from "../WidgetName";
import { georgiaCountyPaths, georgiaCountyBubbles, MAP_VIEWBOX_WIDTH, MAP_VIEWBOX_HEIGHT } from "./georgia-counties";
import { normalizeCountyKey, getCountyDisplayName } from "./countyNames";
import { getVisibleBounds, placeTooltip, TOOLTIP_MARGIN } from "./tooltipPosition";
import "./results-map.scss";
import ErrorBoundary from "../ErrorBoundary";
import { getRaceTitle, findRaceCandidate, applyDisplayOverrides, applyStatewideOverrides, getShortCandidateLabel } from "./overrides";

const normalizeRU = (ru, race) => {
  const countyName = normalizeCountyKey(ru.countyName || ru.reportingunitName);
  if (!countyName) return null;

  const candidates = (ru.candidates || []).map((c) => ({
    ...c,
    name: c.name || [c.first || c.firstName, c.last || c.lastName].filter(Boolean).join(" "),
    votes: c.votes ?? c.voteCount ?? 0,
    percentage: c.percentage ?? 0,
    party: c.party || "",
    winner: getOverride(c, "winner") === true,
  })).map((c) => applyDisplayOverrides(c, findRaceCandidate(race, c))); // county rows have no overrides; only name/party follow the race-level candidate

  const totalVotes = candidates.reduce((s, c) => s + c.votes, 0);
  const withPct = candidates.map((c) => ({
    ...c,
    percentage: c.percentage || (totalVotes > 0 ? parseFloat((c.votes / totalVotes * 100).toFixed(1)) : 0),
  }));

  const sorted = [...withPct].sort((a, b) => b.votes - a.votes);
  const leader = sorted[0];
  if (!leader || leader.votes === 0) return null;

  const runnerUp = sorted[1];
  const margin = runnerUp
    ? parseFloat((leader.percentage - runnerUp.percentage).toFixed(1))
    : leader.percentage;

  return {
    countyName,
    party: leader.party,
    allCandidates: sorted,
    margin,
    precinctsReporting: ru.precinctsReporting ?? null,
    precinctsTotal: ru.precinctsTotal ?? null,
    // County percent: the county reporting unit's own eevp only; blank when missing, never precinctsPercent.
    eevpPct: getEstimatedVotesPercent(ru),
    fipsCode: ru.fipsCode ? String(ru.fipsCode) : null,
  };
};

const buildCountyResults = (races) => {
  const countyMap = {};
  races.forEach((race) => {
    const rus = race.reportingUnits || [];
    const countyRUs = rus.filter((ru) =>
      ru.countyName || ru.level === "subunit" || ru.reportingunitLevel === 2
    );
    countyRUs.forEach((ru) => {
      const normalized = normalizeRU(ru, race);
      if (!normalized || countyMap[normalized.countyName]) return;
      countyMap[normalized.countyName] = normalized;
    });
  });
  return countyMap;
};

/** County EEVP sentences keyed like countyResults (normalized county name), from each county reporting unit's own `eevp`.
 * Separate from countyResults because normalizeRU drops counties with no votes yet, which still carry a real EEVP (e.g. 0). */
const buildCountyEevpSentences = (races) => {
  const map = {};
  races.forEach((race) => {
    (race.reportingUnits || [])
      .filter((ru) => ru.countyName || ru.level === "subunit" || ru.reportingunitLevel === 2)
      .forEach((ru) => {
        const key = normalizeCountyKey(ru.countyName || ru.reportingunitName);
        const sentence = getRaceEevpSentence(ru);
        if (key && sentence && !map[key]) map[key] = sentence;
      });
  });
  return map;
};

const getStateResult = (races) => {
  for (const race of races) {
    const rus = race.reportingUnits || [];
    const stateRU = rus.find((ru) =>
      ru.level === "state" || ru.reportingunitLevel === 1 ||
      (!ru.countyName && !ru.reportingunitName && ru.candidates?.length)
    );
    const src = stateRU || (race.candidates?.length ? race : null);
    if (!src) continue;
    const rawCandidates = stateRU ? stateRU.candidates : race.candidates;
    const candidates = (rawCandidates || []).map((c) => ({
      ...c,
      name: c.name || [c.first || c.firstName, c.last || c.lastName].filter(Boolean).join(" "),
      votes: c.votes ?? c.voteCount ?? 0,
      percentage: c.percentage ?? 0,
      winner: getOverride(c, "winner") === true,
    })).map((c) => applyStatewideOverrides(c, findRaceCandidate(race, c)));
    const sorted = [...candidates].sort((a, b) => b.votes - a.votes);
    if (sorted[0] && sorted[0].votes > 0) {
      return {
        raceName: getRaceTitle(race),
        // Overall race-level sentence from race.eevp, or null (then nothing is shown); same race as picked here.
        eevpSentence: getRaceEevpSentence(race),
        allCandidates: sorted,
      };
    }
  }
  return null;
};

const MAX_BUBBLE_R = 20;

/** Choropleth path with hover border */
const CountyPath = ({ county, countyResults, countyEevp, selectedCounty, onSelect, onHover }) => {
  const [hovered, setHovered] = useState(false);
  const normalizedName = normalizeCountyKey(county.name);
  const result = countyResults[normalizedName];
  const eevpSentence = countyEevp[normalizedName] || null;
  const isSelected = !!selectedCounty && selectedCounty === normalizedName;
  const isDimmed = !!selectedCounty && !isSelected && !!result;

  const fill = result ? getAffiliationColor(result.party) : "#d0d0d0";
  // Flat 0.35 — matches map.html's actual baseline fill-opacity for county shapes exactly
  // (color itself still comes from getAffiliationColor, per your earlier confirmation).
  const fillOpacity = isDimmed ? 0.25 : 0.35;
  const stroke = isSelected ? "#E8A000" : hovered ? "#666666" : "#CCCCCC";
  const strokeWidth = isSelected ? 2.5 : hovered ? 1.5 : 0.5;

  return (
    <path
      data-rm-county={county.name}
      d={county.path}
      fill={fill}
      fillOpacity={fillOpacity}
      stroke={stroke}
      strokeWidth={strokeWidth}
      style={{ cursor: "pointer", transition: "fill-opacity 0.2s, stroke-width 0.15s" }}
      onMouseEnter={(e) => { setHovered(true); onHover(county.name, result, e, eevpSentence); }}
      onMouseMove={(e) => onHover(county.name, result, e, eevpSentence)}
      onMouseLeave={() => { setHovered(false); onHover(null, null, null, null); }}
      onClick={() => result && onSelect(normalizedName, county.name)}
    />
  );
};

/** Bubble with hover border */
const CountyBubble = ({ bubble, countyResults, countyEevp, maxVotes, selectedCounty, onSelect, onHover }) => {
  const [hovered, setHovered] = useState(false);
  const normalizedName = normalizeCountyKey(bubble.name);
  const result = countyResults[normalizedName];
  const eevpSentence = countyEevp[normalizedName] || null;
  const isSelected = !!selectedCounty && selectedCounty === normalizedName;
  const isDimmed = !!selectedCounty && !isSelected && !!result;

  const totalVotes = result ? result.allCandidates.reduce((s, c) => s + c.votes, 0) : 0;
  const r = maxVotes > 0 && totalVotes > 0
    ? Math.max(2, Math.sqrt(totalVotes / maxVotes) * MAX_BUBBLE_R)
    : 2;

  const fill = result ? getAffiliationColor(result.party) : "#d0d0d0";
  const fillOpacity = isDimmed ? 0.25 : 1;
  const stroke = isSelected ? "#E8A000" : hovered ? "#555555" : "#FFFFFF";
  const strokeWidth = isSelected ? 2.5 : hovered ? 1.5 : 0.5;

  return (
    <circle
      data-rm-county={bubble.name}
      cx={bubble.cx}
      cy={bubble.cy}
      r={r}
      fill={fill}
      fillOpacity={fillOpacity}
      stroke={stroke}
      strokeWidth={strokeWidth}
      style={{ cursor: "pointer", transition: "r 0.15s, fill-opacity 0.2s, stroke-width 0.15s" }}
      onMouseEnter={(e) => { setHovered(true); onHover(bubble.name, result, e, eevpSentence); }}
      onMouseMove={(e) => onHover(bubble.name, result, e, eevpSentence)}
      onMouseLeave={() => { setHovered(false); onHover(null, null, null, null); }}
      onClick={() => result && onSelect(normalizedName, bubble.name)}
    />
  );
};

/** Finds the candidate in a county's list matching a statewide reference candidate's identity (id, else name) — not by local vote rank, since a county's local leader may not be the statewide leader */
const matchCandidate = (candidates, ref) => {
  if (!ref || !candidates?.length) return null;
  if (ref.id != null) {
    const byId = candidates.find((c) => c.id != null && String(c.id) === String(ref.id));
    if (byId) return byId;
  }
  const refName = (ref.matchName || ref.name || "").toLowerCase();
  return candidates.find((c) => (c.matchName || c.name || "").toLowerCase() === refName) || null;
};

const partyLabel = (party) => {
  const p = (party || "").toLowerCase();
  if (p === "dem" || p === "d") return "D";
  if (p === "gop" || p === "rep" || p === "r") return "R";
  if (p === "lib") return "L";
  return party.toUpperCase().slice(0, 1);
};

/** SVG legend rendered inside the map SVG — matches POC's translate(30,530) row exactly, including the reporting count text at the far right of the same row */
const MapLegend = ({ stateResult, reportingCount, totalCounties }) => {
  const topTwo = stateResult ? stateResult.allCandidates.filter((c) => c.votes > 0).slice(0, 2) : [];
  const c0 = topTwo[0];
  const c1 = topTwo[1];
  const c0Color = c0 ? getAffiliationColor(c0.party) : "#2E6EC7";
  const c1Color = c1 ? getAffiliationColor(c1.party) : "#CC2936";
  const c0Label = c0 ? partyLabel(c0.party) : "D";
  const c1Label = c1 ? partyLabel(c1.party) : "R";

  return (
    <g transform="translate(30, 495)">
      <defs>
        <linearGradient id="legend-margin-grad" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor={c0Color} stopOpacity="1" />
          <stop offset="35%" stopColor={c0Color} stopOpacity="0.4" />
          <stop offset="50%" stopColor="#888888" stopOpacity="0.25" />
          <stop offset="65%" stopColor={c1Color} stopOpacity="0.4" />
          <stop offset="100%" stopColor={c1Color} stopOpacity="1" />
        </linearGradient>
      </defs>
      <text x="0" y="-4" style={{ fontFamily: "sans-serif", fontSize: 10, fontWeight: 600, fill: "#6e6e6e" }}>Margin</text>
      <rect x="0" y="0" width="240" height="10" rx="3" fill="url(#legend-margin-grad)" />
      <text x="0" y="22" style={{ fontFamily: "sans-serif", fontSize: 10, fill: c0Color, fontWeight: 700 }}>{c0Label}</text>
      <text x="120" y="22" textAnchor="middle" style={{ fontFamily: "sans-serif", fontSize: 9, fill: "#888" }}>Even</text>
      <text x="240" y="22" textAnchor="end" style={{ fontFamily: "sans-serif", fontSize: 10, fill: c1Color, fontWeight: 700 }}>{c1Label}</text>
      {totalCounties > 0 && (
        <text x="420" y="8" textAnchor="end" style={{ fontFamily: "var(--ec-font-sans)", fontSize: 11, fontWeight: 500, fill: "var(--ec-ink-3)" }}>
          {reportingCount} of {totalCounties} counties reporting
        </text>
      )}
    </g>
  );
};

const TOOLTIP_MAX_WIDTH = 300;
const TOOLTIP_MAX_WIDTH_TOUCH = 260;

/** Map hover tooltip — matches POC screenshot: swatch/name/pct/votes rows + margin footer, no bars.
 * Rendered as a child of .ec-content (outside the map card's overflow:hidden) and positioned with
 * position:absolute from its measured size, so it scrolls with the map and is clamped to the visible viewport.
 * pos = { x, y, touch } in client coordinates. */
const MapTooltip = ({ countyName, result, eevpSentence, pos }) => {
  const ref = useRef(null);
  const visible = !!countyName && !!pos;
  useLayoutEffect(() => {
    const el = ref.current;
    const parent = el && el.offsetParent;
    if (!el || !parent || !pos) return;
    const bounds = getVisibleBounds();
    const maxWidth = Math.min(pos.touch ? TOOLTIP_MAX_WIDTH_TOUCH : TOOLTIP_MAX_WIDTH, bounds.right - bounds.left - 2 * TOOLTIP_MARGIN);
    el.style.maxWidth = `${maxWidth}px`;
    const { left, top } = placeTooltip({ x: pos.x, y: pos.y, width: el.offsetWidth, height: el.offsetHeight, bounds, touch: pos.touch });
    const origin = parent.getBoundingClientRect();
    el.style.left = `${left - origin.left - parent.clientLeft}px`;
    el.style.top = `${top - origin.top - parent.clientTop}px`;
  }, [pos, countyName, result, eevpSentence]);
  if (!visible) return null;
  const displayName = getCountyDisplayName(countyName);
  const candidates = result ? result.allCandidates.filter((c) => c.votes > 0).slice(0, 2) : [];
  const totalVotes = candidates.reduce((s, c) => s + c.votes, 0);
  const [leader, runnerUp] = candidates;

  const metaParts = [];
  if (result?.precinctsReporting != null && result?.precinctsTotal != null) {
    metaParts.push(`${result.precinctsReporting}/${result.precinctsTotal} precincts`);
  }
  if (totalVotes > 0) metaParts.push(`${totalVotes.toLocaleString()} votes`);

  return (
    <div className="rm-map-tooltip" ref={ref}>
      <div className="rm-map-tooltip-header">
        <div className="rm-map-tooltip-name">{displayName} County</div>
        {metaParts.length > 0 && <div className="rm-map-tooltip-meta">{metaParts.join(" · ")}</div>}
        {eevpSentence && <div className="rm-map-tooltip-eevp">{eevpSentence}</div>}
      </div>
      {candidates.length ? (
        <div className="rm-map-tooltip-body">
          {candidates.map((c, i) => {
            const color = getAffiliationColor(c.party);
            return (
              <div key={i} className="rm-map-tooltip-row">
                <span className="rm-map-tooltip-swatch" style={{ background: color }} />
                <span className="rm-map-tooltip-cand">{c.name || getShortCandidateLabel(c)}</span>
                <span className="rm-map-tooltip-pct">{c.percentage}%</span>
                <span className="rm-map-tooltip-votes-inline">({c.votes.toLocaleString()})</span>
              </div>
            );
          })}
          {leader && runnerUp && result?.margin != null && (
            <div className="rm-map-tooltip-margin">Margin: {leader.name || getShortCandidateLabel(leader)} +{result.margin}</div>
          )}
        </div>
      ) : (
        <div className="rm-map-tooltip-nodata">No data</div>
      )}
    </div>
  );
};

/** Statewide header — matches POC ec-card-header with candidate cards + precincts bar */
const StatewideHeader = ({ stateResult }) => {
  if (!stateResult) return null;
  const { raceName, eevpSentence, allCandidates } = stateResult;
  const topTwo = allCandidates.filter((c) => c.votes > 0).slice(0, 2);

  return (
    <div className="ec-card-header">
      <div>
        <div className="ec-card-title">{raceName}</div>
        {eevpSentence && <div className="ec-card-subtitle">{eevpSentence}</div>}
      </div>
      <div className="ec-candidates">
        {topTwo.map((c, i) => {
          const color = getAffiliationColor(c.party);
          return (
            <div key={i} className="ec-candidate">
              <span className="ec-party-dot" style={{ background: color }} />
              <span className="ec-candidate-name">{c.name || getShortCandidateLabel(c)}</span>
              <span className="ec-candidate-pct" style={{ color }}>
                {c.percentage}%{c.winner && <span className="rm-winner-check">✓</span>}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** Statewide summary cards inside table col — matches POC exactly */
const StatewideSummaryCards = ({ stateResult }) => {
  if (!stateResult) return null;
  const { eevpSentence, allCandidates } = stateResult;
  const topTwo = allCandidates.filter((c) => c.votes > 0).slice(0, 2);

  return (
    <div className="rm-summary-wrap">
      <div className="rm-summary-cards">
        {topTwo.map((c, i) => {
          const color = getAffiliationColor(c.party);
          const partyLabel = c.party === "GOP" || c.party === "Rep" ? "R"
            : c.party === "Dem" ? "D"
            : c.party;
          return (
            <div key={i} className="rm-summary-card" style={{ borderTopColor: color }}>
              <div className="rm-summary-card-top">
                <span className="rm-summary-name">{c.name || getShortCandidateLabel(c)}</span>
                <span className="rm-summary-party" style={{ color }}>{partyLabel}</span>
              </div>
              <div className="rm-summary-pct-row">
                <span className="rm-summary-pct">{c.percentage}%</span>
                <span className="rm-summary-votes">{c.votes.toLocaleString()}</span>
              </div>
              <div className="ec-bar-track">
                <div className="ec-bar-fill" style={{ width: `${c.percentage}%`, background: color }} />
              </div>
            </div>
          );
        })}
      </div>
      {eevpSentence && <div className="rm-eevp-summary">{eevpSentence}</div>}
    </div>
  );
};

/** County detail card */
const CountyDetailCard = ({ countyName, result, onClose }) => {
  if (!result) return null;
  const { allCandidates, precinctsReporting, precinctsTotal, eevpPct, margin } = result;
  const totalVotes = allCandidates.reduce((s, c) => s + c.votes, 0);
  const leader = allCandidates[0];
  const runnerUp = allCandidates[1];

  return (
    <div className="ec-card rm-detail-card">
      <div className="ec-county-detail">
        <div className="ec-county-header">
          <div>
            <div className="ec-county-name">{getCountyDisplayName(countyName)} County</div>
            <div className="ec-county-precincts">
              {precinctsReporting != null
                ? `${precinctsReporting} / ${precinctsTotal} precincts reporting`
                : eevpPct != null
                ? `${eevpPct}% precincts reporting`
                : ""}
              {margin != null && ` · Margin: ${leader && runnerUp ? `${leader.name || getShortCandidateLabel(leader)} +${margin}%` : `${margin}%`}`}
              {` · ${totalVotes.toLocaleString()} total votes`}
            </div>
          </div>
          <button className="ec-btn" onClick={onClose}>Close</button>
        </div>
        <div className="ec-county-candidates">
          {allCandidates.filter((c) => c.votes > 0).map((c, i) => {
            const color = getAffiliationColor(c.party);
            const partyLabel = c.party === "GOP" || c.party === "Rep" ? "R"
              : c.party === "Dem" ? "D"
              : c.party;
            return (
              <div key={i} className="ec-county-candidate" style={{ borderLeftColor: color }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <span className="ec-county-candidate-name">{c.name}</span>
                  <span className="ec-party-badge" style={{ background: color }}>{partyLabel}</span>
                </div>
                <div className="ec-county-pct" style={{ color }}>{c.percentage}%</div>
                <div className="ec-county-votes">{c.votes.toLocaleString()} votes</div>
                <div className="ec-bar-track">
                  <div className="ec-bar-fill" style={{ width: `${c.percentage}%`, background: color }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const CountyTable = ({ stateResult, countyResults, selectedCounty, onSelect, onClear, selectedRowRef }) => {
  const [sortCol, setSortCol] = useState(0);
  const [sortAsc, setSortAsc] = useState(true);
  // Header dropdown search (the only search box)
  const [dropdownQuery, setDropdownQuery] = useState("");
  const [searchDropdown, setSearchDropdown] = useState([]);
  const [activeIdx, setActiveIdx] = useState(-1);
  // True while the selected county was picked from this search box (so clearing the text clears that selection).
  const [pickedFromSearch, setPickedFromSearch] = useState(false);

  const rows = useMemo(() => {
    return Object.entries(countyResults).map(([name, r]) => ({
      name,
      displayName: getCountyDisplayName(name),
      fipsCode: r.fipsCode,
      precinctsReporting: r.precinctsReporting,
      precinctsTotal: r.precinctsTotal,
      eevpPct: r.eevpPct,
      allCandidates: r.allCandidates,
      margin: r.margin,
      party: r.party,
    }));
  }, [countyResults]);

  const firstRow = useMemo(() => rows[0] || null, [rows]);
  // Use stateResult for column labels if available, else fall back to first county row
  const c1Cand = stateResult?.allCandidates[0] || firstRow?.allCandidates[0];
  const c2Cand = stateResult?.allCandidates[1] || firstRow?.allCandidates[1];
  const c1Label = getShortCandidateLabel(c1Cand) || "Cand 1";
  const c2Label = getShortCandidateLabel(c2Cand) || "Cand 2";
  const c1Color = c1Cand ? getAffiliationColor(c1Cand.party) : "#999";
  const c2Color = c2Cand ? getAffiliationColor(c2Cand.party) : "#999";

  // Match each row's candidates to the statewide c1Cand/c2Cand by identity, not by local vote rank —
  // a county's local leader isn't always the statewide leader, so row.allCandidates[0] can be the wrong person for these fixed columns.
  const rowsWithCols = useMemo(() => {
    return rows.map((row) => ({
      ...row,
      col1: matchCandidate(row.allCandidates, c1Cand),
      col2: matchCandidate(row.allCandidates, c2Cand),
    }));
  }, [rows, c1Cand, c2Cand]);

  const sorted = useMemo(() => {
    const arr = [...rowsWithCols];
    arr.sort((a, b) => {
      let av, bv;
      if (sortCol === 0) { av = a.displayName; bv = b.displayName; }
      else if (sortCol === 1) {
        // Sort by the county eevp only; counties without one sort lowest.
        av = a.eevpPct ?? -1;
        bv = b.eevpPct ?? -1;
      } else if (sortCol === 2) { av = a.col1?.votes || 0; bv = b.col1?.votes || 0; }
      else if (sortCol === 3) { av = a.col1?.percentage || 0; bv = b.col1?.percentage || 0; }
      else if (sortCol === 4) { av = a.col2?.votes || 0; bv = b.col2?.votes || 0; }
      else if (sortCol === 5) { av = a.col2?.percentage || 0; bv = b.col2?.percentage || 0; }
      else if (sortCol === 7) { av = a.margin || 0; bv = b.margin || 0; }
      else { av = a.displayName; bv = b.displayName; }

      // Compute the comparison in a fixed "ascending" sense, tiebreak by name when the primary
      // value ties (e.g. most counties sitting at 100% precincts reporting), then flip at the end
      // based on sortAsc. Array.prototype.sort is stable, so ties otherwise keep their pre-sort
      // order NO MATTER which direction you sort in — toggling direction would look like it does
      // nothing once the only non-tied rows have already been moved by the first click.
      let cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
      if (cmp === 0 && sortCol !== 0) cmp = a.displayName.localeCompare(b.displayName);
      return sortAsc ? cmp : -cmp;
    });
    return arr;
  }, [rowsWithCols, sortCol, sortAsc]);

  const handleSort = (col) => {
    if (sortCol === col) setSortAsc((a) => !a);
    else { setSortCol(col); setSortAsc(col === 0); }
  };

  // Dropdown search handlers
  const handleDropdownSearch = (q) => {
    setDropdownQuery(q);
    if (!q.trim()) {
      setSearchDropdown([]);
      // Clearing the text also clears the county that was picked from this search (map highlight, row and card).
      if (pickedFromSearch) {
        setPickedFromSearch(false);
        if (onClear) onClear();
      }
      return;
    }
    const matches = rows.filter((r) => r.displayName.toLowerCase().includes(normalizeCountyKey(q))).slice(0, 8);
    setSearchDropdown(matches);
    setActiveIdx(-1);
  };

  const pickSearch = (row) => {
    setDropdownQuery(row.displayName + " County");
    setSearchDropdown([]);
    setPickedFromSearch(true);
    onSelect(row.name, row.displayName);
  };

  const handleKeyDown = (e) => {
    if (!searchDropdown.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, searchDropdown.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && activeIdx >= 0) { pickSearch(searchDropdown[activeIdx]); }
    else if (e.key === "Escape") { setSearchDropdown([]); }
  };

  const cols = [
    { label: "County", align: "left" },
    { label: "Precincts", align: "center" },
    { label: `${c1Label} Votes`, align: "right", hideMobile: true },
    { label: `${c1Label} %`, align: "right" },
    { label: `${c2Label} Votes`, align: "right", hideMobile: true },
    { label: `${c2Label} %`, align: "right" },
    { label: "Split", align: "center", sortable: false },
    { label: "Margin", align: "right" },
  ];

  return (
    <div className="ec-table-col ec-card">
      {/* Header with dropdown search */}
      <div className="ec-card-header">
        <div>
          <div className="ec-card-title">County Results</div>
          <div className="ec-card-subtitle">{rows.length} counties — Click a row to highlight on map</div>
        </div>
        <div className="ec-search-wrapper">
          <div className="ec-search-input-wrap">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="ec-search-icon">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              className="ec-search-input"
              placeholder="Search county, city..."
              value={dropdownQuery}
              onChange={(e) => handleDropdownSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={() => setTimeout(() => setSearchDropdown([]), 150)}
            />
          </div>
          {searchDropdown.length > 0 && (
            <div className="ec-search-dropdown">
              {searchDropdown.map((r, i) => (
                <button
                  key={r.name}
                  className={`ec-search-item${i === activeIdx ? " ec-search-item--active" : ""}`}
                  onMouseDown={() => pickSearch(r)}
                >
                  <span className="ec-search-item-label">{r.displayName} County</span>
                  <span className="ec-search-item-context">County</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Statewide summary cards — outside scroll area */}
      <StatewideSummaryCards stateResult={stateResult} />

      {/* Scrollable table only */}
      <div className="rm-table-scroll">
        <table className="rm-table">
            <thead>
              <tr>
                {cols.map((col, i) => (
                  <th
                    key={i}
                    className={[col.hideMobile ? "rt-hide-mobile" : "", i === 0 ? "rm-table-sticky-col" : ""].filter(Boolean).join(" ")}
                    style={{ textAlign: col.align, cursor: col.sortable === false ? "default" : "pointer" }}
                    onClick={col.sortable === false ? undefined : () => handleSort(i)}
                  >
                    {col.label}
                    {col.sortable !== false && (
                      <span className="rm-sort-icon" style={{ opacity: sortCol === i ? 1 : 0.3 }}>
                        {sortCol === i ? (sortAsc ? " ▲" : " ▼") : " ▲"}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => {
                const isSelected = row.name === selectedCounty;
                const col1 = row.col1;
                const col2 = row.col2;
                const localLeader = row.allCandidates[0];
                const localRunnerUp = row.allCandidates[1];
                const precPct = row.eevpPct;
                const marginLabel = localLeader && localRunnerUp
                  ? `${getShortCandidateLabel(localLeader)} +${row.margin}%`
                  : row.margin != null ? `${row.margin}%` : "—";

                return (
                  <tr
                    key={row.name}
                    ref={isSelected ? selectedRowRef : null}
                    onClick={() => onSelect(row.name, row.displayName)}
                    style={isSelected ? {
                      background: "rgba(232,160,0,0.14)",
                      cursor: "pointer",
                    } : {
                      cursor: "pointer",
                    }}
                  >
                    <td
                      className="rm-table-sticky-col"
                      style={{
                        textAlign: "left",
                        /* opaque (not the row's translucent rgba) — this cell stays painted over
                           other columns while sticky during horizontal scroll, so it can't be see-through */
                        ...(isSelected ? {
                          background: "#fbf1da",
                          boxShadow: "rgb(232,160,0) 3px 0 0 0 inset",
                        } : {}),
                      }}
                    >
                      {row.displayName}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <div className="rm-precincts-cell">
                        {precPct != null && (
                          <div className="rm-precincts-bar-track">
                            <div className="rm-precincts-bar-fill" style={{ width: `${precPct}%` }} />
                          </div>
                        )}
                        <span>{row.precinctsReporting != null ? `${row.precinctsReporting} / ${row.precinctsTotal}` : precPct != null ? `${precPct}%` : "—"}</span>
                      </div>
                    </td>
                    <td className="rt-hide-mobile" style={{ textAlign: "right", fontWeight: 700, color: c1Color }}>{col1 ? col1.votes.toLocaleString() : "—"}</td>
                    <td style={{ textAlign: "right", fontWeight: 700, color: c1Color }}>{col1 ? `${col1.percentage}%` : "—"}</td>
                    <td className="rt-hide-mobile" style={{ textAlign: "right", color: c2Color }}>{col2 ? col2.votes.toLocaleString() : "—"}</td>
                    <td style={{ textAlign: "right", color: c2Color }}>{col2 ? `${col2.percentage}%` : "—"}</td>
                    <td style={{ textAlign: "left", verticalAlign: "middle" }}>
                      {col1 && col2 && (
                        <div className="rm-split-track">
                          <div className="rm-split-fill" style={{ width: `${col1.percentage}%`, background: c1Color }} />
                          <div className="rm-split-fill" style={{ width: `${col2.percentage}%`, background: c2Color }} />
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: "right", color: getAffiliationColor(localLeader?.party), fontWeight: 600 }}>{marginLabel}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
    </div>
  );
};

const EMPTY_TOOLTIP = { name: null, result: null, eevpSentence: null, pos: null };
const TOUCH_MOUSE_WINDOW_MS = 1000;

const ResultsMapWidget = (props) => {
  const races = useMemo(() => props.races || [], [props.races]);
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [selectedCountyName, setSelectedCountyName] = useState(null);
  const [tooltip, setTooltip] = useState(EMPTY_TOOLTIP);
  const selectedRowRef = useRef(null);
  const lastTouchRef = useRef(0);

  const handleMapHover = useCallback((name, result, e, eevpSentence) => {
    // A card opened by a tap stays open when the finger/pointer "leaves" (touch browsers fire a synthetic mouseleave
    // when you tap or scroll elsewhere). It closes on a second tap of the same county instead.
    if (!name || !e) { setTooltip((t) => (t.pos && t.pos.touch ? t : EMPTY_TOOLTIP)); return; }
    // After a tap the browser fires compatibility mouse events (mouseenter/move) at the tap point;
    // treat mouse events that closely follow a touch as touch, so a tap gets the touch placement.
    const touch = Date.now() - lastTouchRef.current < TOUCH_MOUSE_WINDOW_MS;
    setTooltip({ name, result, eevpSentence, pos: { x: e.clientX, y: e.clientY, touch } });
  }, []);

  // Remember when the last touch happened, so the mouse events a tap produces get the touch placement.
  // A tapped card is NOT closed by scrolling, by tapping elsewhere, or by a timer: it stays until the same county
  // is tapped a second time (handleSelect), another county is tapped (the card moves), or the detail panel is closed.
  useEffect(() => {
    const markTouch = () => { lastTouchRef.current = Date.now(); };
    const opts = { capture: true, passive: true };
    document.addEventListener("touchstart", markTouch, opts);
    document.addEventListener("touchend", markTouch, opts);
    return () => {
      document.removeEventListener("touchstart", markTouch, opts);
      document.removeEventListener("touchend", markTouch, opts);
    };
  }, []);

  const rawStateResult = useMemo(() => getStateResult(races), [races]);
  const countyResults = useMemo(() => buildCountyResults(races), [races]);
  const countyEevp = useMemo(() => buildCountyEevpSentences(races), [races]);

  // The statewide sentence comes only from the race eevp; it is never aggregated
  // from county counts. A null sentence hides it.
  const stateResult = rawStateResult;

  const maxVotes = useMemo(() => {
    return Object.values(countyResults).reduce((max, r) => {
      const total = r.allCandidates.reduce((s, c) => s + c.votes, 0);
      return total > max ? total : max;
    }, 0);
  }, [countyResults]);

  const reportingCount = useMemo(() => {
    return Object.values(countyResults).filter(
      (r) => (r.precinctsReporting ?? 0) > 0
    ).length;
  }, [countyResults]);

  const selectedResult = useMemo(() => {
    if (!selectedCounty) return null;
    return countyResults[selectedCounty] || null;
  }, [selectedCounty, countyResults]);

  const handleSelect = useCallback((countyKey, countyName) => {
    if (!countyKey) return;
    if (selectedCounty === countyKey) {
      // Second tap on the same county (map or table row) closes the selection and its card.
      setSelectedCounty(null);
      setSelectedCountyName(null);
      setTooltip(EMPTY_TOOLTIP);
    } else {
      setSelectedCounty(countyKey);
      setSelectedCountyName(countyName);
      // Picking a county from the table (or another county) must not leave a stale card for a different county.
      setTooltip((t) => (t.name && normalizeCountyKey(t.name) !== countyKey ? EMPTY_TOOLTIP : t));
      // On phones/tablets the county card shows on tap, so don't scroll the PAGE down to the table row
      // (it carries the map and the card out of view). The table's own scroll area still moves to the selected
      // row, so the row is visible when the user scrolls down to it. Desktop keeps the scroll-to-row behaviour.
      const isMobile =
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(max-width: 768px), (pointer: coarse)").matches;
      setTimeout(() => {
        const row = selectedRowRef.current;
        if (!row) return;
        if (!isMobile) {
          row.scrollIntoView({ block: "center", behavior: "smooth" });
          return;
        }
        const scroller = row.closest ? row.closest(".rm-table-scroll") : null;
        if (!scroller) return;
        const rowRect = row.getBoundingClientRect();
        const scrollerRect = scroller.getBoundingClientRect();
        const delta = rowRect.top - scrollerRect.top - (scrollerRect.height - rowRect.height) / 2;
        scroller.scrollTo({ top: Math.max(0, scroller.scrollTop + delta), behavior: "smooth" });
      }, 50);
    }
  }, [selectedCounty]);

  const handleClose = useCallback(() => {
    setSelectedCounty(null);
    setSelectedCountyName(null);
    setTooltip(EMPTY_TOOLTIP);
  }, []);

  return (
    <div className="ec-dashboard" data-theme="light">
      <div className="ec-content">
        <WidgetName widget={props.widget} updated={props.updated} />

        <div className="ec-map-row">
          {/* Left: Map wrapped in reporting box */}
          <div className="ec-map-col">
            <div className="ec-card">
              <StatewideHeader stateResult={stateResult} />
              <div className="ec-card-body">
                <div className="rm-map-frame">
                  <svg
                    viewBox={`0 0 ${MAP_VIEWBOX_WIDTH} ${MAP_VIEWBOX_HEIGHT}`}
                    preserveAspectRatio="xMidYMid meet"
                    className="rm-map-svg"
                    role="img"
                    aria-label="Georgia county results map"
                  >
                    <g className="county-boundaries">
                      {georgiaCountyPaths.map((county, i) => (
                        <ErrorBoundary key={i} message="">
                          <CountyPath
                            county={county}
                            countyResults={countyResults}
                            countyEevp={countyEevp}
                            selectedCounty={selectedCounty}
                            onSelect={handleSelect}
                            onHover={handleMapHover}
                          />
                        </ErrorBoundary>
                      ))}
                    </g>
                    <g>
                      {georgiaCountyBubbles.map((bubble, i) => (
                        <ErrorBoundary key={i} message="">
                          <CountyBubble
                            bubble={bubble}
                            countyResults={countyResults}
                            countyEevp={countyEevp}
                            maxVotes={maxVotes}
                            selectedCounty={selectedCounty}
                            onSelect={handleSelect}
                            onHover={handleMapHover}
                          />
                        </ErrorBoundary>
                      ))}
                    </g>
                    <MapLegend stateResult={stateResult} reportingCount={reportingCount} totalCounties={georgiaCountyPaths.length} />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Table */}
          <CountyTable
            stateResult={stateResult}
            countyResults={countyResults}
            selectedCounty={selectedCounty}
            onSelect={handleSelect}
            onClear={handleClose}
            selectedRowRef={selectedRowRef}
          />
        </div>

        {/* Detail card */}
        {selectedCounty && selectedResult && (
          <CountyDetailCard
            countyName={selectedCountyName}
            result={selectedResult}
            onClose={handleClose}
          />
        )}

        <MapTooltip countyName={tooltip.name} result={tooltip.result} eevpSentence={tooltip.eevpSentence} pos={tooltip.pos} />
      </div>
    </div>
  );
};

export default ResultsMapWidget;
